/**
 * link-rel.js
 * ---------------------------------------------------------------------------
 * FD-1247 - Apply rel="nofollow" to designated outbound URLs.
 *
 * Safety net that applies rel attributes to outbound links the Liquid
 * templates cannot reach: page rich-text content, blog articles, and markup
 * injected by third-party apps.
 *
 * NOTE ON SEO EFFECTIVENESS
 * Googlebot extracts link attributes primarily during its initial HTML parse.
 * JS rendering is queued separately and is not guaranteed on every crawl, so
 * a rel added here is a WEAKER signal than one rendered server-side by Liquid.
 * Anything you control in a .liquid file should be fixed there instead
 * (see snippets/link-rel.liquid). This script covers the remainder, plus
 * `noopener`, which is a browser security fix and works reliably from JS.
 *
 * Existing rel values are preserved; this only adds missing tokens.
 */
(function () {
  'use strict';

  /**
   * Hostnames that are ours even though they are not the current domain.
   * Links to these are treated as internal and left alone.
   */
  var FIRST_PARTY_HOSTS = [
    'favoritedaughter.com',
    'shopfavoritedaughter.com',
    'cdn.shopify.com'
  ];

  /**
   * Rel tokens applied to every outbound link that has no more specific rule.
   */
  var DEFAULT_REL = ['nofollow', 'noopener'];

  /**
   * Exact whole-URL targets.
   *
   * These are matched against the anchor's fully-resolved href, normalised
   * (see normalise() below) so that a trailing slash, a "www." prefix, or a
   * differing query-string order does not cause a miss. Matching the WHOLE
   * URL means only these specific pages are tagged - other pages on the same
   * domain are left to the default outbound rule.
   */
  var URL_RULES = [
    {
      url: 'https://shopfavoritedaughter.treet.co/',
      rel: ['nofollow', 'noopener']
    },
    {
      url: 'https://www.centricbrands.com/transparency-in-supply-chains-statement',
      rel: ['nofollow', 'noopener']
    },
    {
      url: 'https://shopfavoritedaughter.gorgias.help/en-US',
      rel: ['nofollow', 'noopener']
    },
    {
      url: 'https://careers-centricbrands.icims.com/jobs/search?ss=1&searchKeyword=%22Favorite+Daughter%22',
      rel: ['nofollow', 'noopener']
    },
    {
      url: 'https://shopify.com/49159143577/account/',
      rel: ['nofollow', 'noopener']
    }
  ];

  /**
   * When true, any outbound link that is NOT in URL_RULES also gets
   * DEFAULT_REL. Set to false to tag ONLY the exact URLs listed above.
   */
  var TAG_ALL_OUTBOUND = true;

  function isOutbound(anchor) {
    // anchor.protocol/hostname are resolved by the browser, so relative
    // hrefs correctly report the current host. Non-http schemes are skipped.
    if (anchor.protocol !== 'http:' && anchor.protocol !== 'https:') {
      return false;
    }
    if (!anchor.hostname || anchor.hostname === window.location.hostname) {
      return false;
    }
    for (var i = 0; i < FIRST_PARTY_HOSTS.length; i++) {
      var host = FIRST_PARTY_HOSTS[i];
      if (anchor.hostname === host || anchor.hostname.endsWith('.' + host)) {
        return false;
      }
    }
    return true;
  }

  /** Adds tokens to an anchor's rel without discarding what is already there. */
  function addRel(anchor, tokens) {
    var existing = (anchor.getAttribute('rel') || '')
      .split(/\s+/)
      .filter(Boolean);

    tokens.forEach(function (token) {
      if (existing.indexOf(token) === -1) {
        existing.push(token);
      }
    });

    if (existing.length) {
      anchor.setAttribute('rel', existing.join(' '));
    }
  }

  /**
   * Reduces a URL to a canonical form so two spellings of the same page
   * compare equal. Handles: case, a "www." prefix, a trailing slash, the
   * default port, a #fragment, and query parameters given in another order.
   * Returns '' when the URL cannot be parsed.
   */
  function normalise(rawUrl) {
    var parsed;
    try {
      parsed = new URL(rawUrl, window.location.href);
    } catch (e) {
      return '';
    }

    var host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    var path = parsed.pathname.replace(/\/+$/, '');

    // Sort query params so ?a=1&b=2 and ?b=2&a=1 canonicalise identically.
    var query = '';
    if (parsed.search && parsed.search.length > 1) {
      var pairs = parsed.search.slice(1).split('&').filter(Boolean);
      pairs.sort();
      query = '?' + pairs.join('&');
    }

    // Protocol and #fragment are deliberately dropped: http/https and a
    // fragment both address the same document for linking purposes.
    return host + path + query;
  }

  /** Pre-computed canonical forms of URL_RULES, built once at startup. */
  var normalisedRules = URL_RULES.map(function (rule) {
    return { key: normalise(rule.url), rel: rule.rel };
  }).filter(function (rule) {
    return rule.key !== '';
  });

  /**
   * Returns the rel tokens for an anchor, or null when it should be left
   * untouched. Exact whole-URL rules win; the default only applies when
   * TAG_ALL_OUTBOUND is on.
   */
  function relFor(anchor) {
    var key = normalise(anchor.href || '');

    for (var i = 0; i < normalisedRules.length; i++) {
      if (normalisedRules[i].key === key) {
        return normalisedRules[i].rel;
      }
    }

    return TAG_ALL_OUTBOUND ? DEFAULT_REL : null;
  }

  function process(root) {
    var scope = root || document;
    var anchors = scope.querySelectorAll('a[href]');

    for (var i = 0; i < anchors.length; i++) {
      var anchor = anchors[i];
      var key = normalise(anchor.href || '');
      var matchedRule = null;

      // An exact whole-URL rule applies whether the target is outbound or
      // internal, so listed URLs are always tagged.
      for (var j = 0; j < normalisedRules.length; j++) {
        if (normalisedRules[j].key === key) {
          matchedRule = normalisedRules[j];
          break;
        }
      }

      if (matchedRule) {
        addRel(anchor, matchedRule.rel);
      } else if (isOutbound(anchor)) {
        var tokens = relFor(anchor);
        if (tokens) {
          addRel(anchor, tokens);
        }
      }
    }
  }

  /**
   * Defers a callback to the next frame, falling back to a timeout where
   * requestAnimationFrame is unavailable (older browsers, some embedded
   * webviews, and background tabs where rAF can be throttled indefinitely).
   */
  function defer(callback) {
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(callback);
    } else {
      window.setTimeout(callback, 16);
    }
  }

  function init() {
    process(document);

    // Apps and widgets inject links after load, so watch for new anchors.
    if (!('MutationObserver' in window)) {
      return;
    }

    var pending = false;
    var observer = new MutationObserver(function (mutations) {
      if (pending) {
        return;
      }
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          pending = true;
          // Batch bursts of DOM insertions into a single pass.
          defer(function () {
            pending = false;
            process(document);
          });
          return;
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
